import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import BookDemoModal from "../components/BookDemoModal";
import RaiseTicketModal from "../components/Support/RaiseTicketModal";
import { useAuth } from "../contexts/AuthContext";
import { getUserTickets } from "../services/ticketService";
import { Ticket } from "../types/ticket";
import {
  Search,
  Book,
  MessageCircle,
  Mail,
  Phone,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  FileText,
  Video,
  Users,
  Settings,
  Shield,
  Zap,
  Code,
  PlayCircle,
  Download,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Clock,
  Globe,
  Database,
  Key,
  Lock,
  Sparkles,
  Plus,
  Ticket as TicketIcon,
} from "lucide-react";

const HelpCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [openFAQ, setOpenFAQ] = useState(null);
  const [openCategory, setOpenCategory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [userTickets, setUserTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [showTickets, setShowTickets] = useState(false);

  // Load user tickets
  useEffect(() => {
    if (user?.id) {
      loadUserTickets();
    }
  }, [user]);

  const loadUserTickets = async () => {
    if (!user?.id) return;
    setLoadingTickets(true);
    try {
      const tickets = await getUserTickets(user.id);
      setUserTickets(tickets);
    } catch (error) {
      console.error("Error loading tickets:", error);
    } finally {
      setLoadingTickets(false);
    }
  };

  const categories = [
    {
      id: "getting-started",
      icon: Book,
      title: "Getting Started",
      description: "Learn the basics of VetriaAI",
      articles: 12,
      color: "bg-blue-100 text-blue-600",
      articlesList: [
        {
          title: "Welcome to VetriaAI",
          description: "Introduction to the platform and its features",
          readTime: "3 min read",
          type: "guide"
        },
        {
          title: "Creating Your First Account",
          description: "Step-by-step guide to setting up your account",
          readTime: "5 min read",
          type: "guide"
        },
        {
          title: "Dashboard Overview",
          description: "Understanding your dashboard and navigation",
          readTime: "4 min read",
          type: "guide"
        },
        {
          title: "Quick Start Guide for HR Teams",
          description: "Get started with hiring in minutes",
          readTime: "6 min read",
          type: "guide"
        },
        {
          title: "Quick Start Guide for Job Seekers",
          description: "Start taking assessments and showcasing skills",
          readTime: "5 min read",
          type: "guide"
        }
      ]
    },
    {
      id: "account-management",
      icon: Users,
      title: "Account Management",
      description: "Manage your account and team",
      articles: 8,
      color: "bg-green-100 text-green-600",
      articlesList: [
        {
          title: "User Roles and Permissions",
          description: "Understanding different user roles and access levels",
          readTime: "5 min read",
          type: "guide"
        },
        {
          title: "Team Management",
          description: "Adding and managing team members",
          readTime: "4 min read",
          type: "guide"
        },
        {
          title: "Profile Settings",
          description: "Updating your profile and preferences",
          readTime: "3 min read",
          type: "guide"
        },
        {
          title: "Billing and Subscriptions",
          description: "Managing your subscription and billing",
          readTime: "6 min read",
          type: "guide"
        }
      ]
    },
    {
      id: "test-creation",
      icon: Settings,
      title: "Test Creation & Management",
      description: "Create and customize assessments",
      articles: 15,
      color: "bg-purple-100 text-purple-600",
      articlesList: [
        {
          title: "Creating Your First Automated Test",
          description: "Complete guide to automated test creation",
          readTime: "8 min read",
          type: "tutorial"
        },
        {
          title: "Customizing Test Questions",
          description: "How to edit and customize generated questions",
          readTime: "6 min read",
          type: "tutorial"
        },
        {
          title: "Test Settings and Configuration",
          description: "Configuring test parameters and settings",
          readTime: "5 min read",
          type: "guide"
        },
        {
          title: "Understanding Assessment Results",
          description: "How to interpret candidate assessment scores",
          readTime: "7 min read",
          type: "guide"
        },
        {
          title: "Test Templates and Question Banks",
          description: "Using and creating test templates",
          readTime: "5 min read",
          type: "guide"
        }
      ]
    },
    {
      id: "security-privacy",
      icon: Shield,
      title: "Security & Privacy",
      description: "Data protection and compliance",
      articles: 6,
      color: "bg-red-100 text-red-600",
      articlesList: [
        {
          title: "Data Security Overview",
          description: "How we protect your data",
          readTime: "4 min read",
          type: "guide"
        },
        {
          title: "Privacy Settings",
          description: "Managing your privacy preferences",
          readTime: "3 min read",
          type: "guide"
        },
        {
          title: "Compliance and Regulations",
          description: "Understanding compliance with data protection laws",
          readTime: "6 min read",
          type: "guide"
        },
        {
          title: "Proctoring System Explained",
          description: "How our 10-layer proctoring system works",
          readTime: "8 min read",
          type: "guide"
        }
      ]
    },
    {
      id: "integrations",
      icon: Zap,
      title: "Integrations & API",
      description: "Connect with your existing tools",
      articles: 10,
      color: "bg-yellow-100 text-yellow-600",
      articlesList: [
        {
          title: "API Documentation",
          description: "Complete API reference and endpoints",
          readTime: "15 min read",
          type: "documentation"
        },
        {
          title: "ATS Integration Guide",
          description: "Integrating with popular ATS platforms",
          readTime: "10 min read",
          type: "tutorial"
        },
        {
          title: "Webhooks Setup",
          description: "Setting up webhooks for real-time updates",
          readTime: "8 min read",
          type: "tutorial"
        },
        {
          title: "Single Sign-On (SSO)",
          description: "Configuring SSO for your organization",
          readTime: "7 min read",
          type: "guide"
        }
      ]
    },
    {
      id: "troubleshooting",
      icon: MessageCircle,
      title: "Troubleshooting",
      description: "Common issues and solutions",
      articles: 9,
      color: "bg-gray-100 text-gray-600",
      articlesList: [
        {
          title: "Common Issues and Solutions",
          description: "Quick fixes for frequently encountered problems",
          readTime: "5 min read",
          type: "guide"
        },
        {
          title: "Browser Compatibility",
          description: "Supported browsers and troubleshooting tips",
          readTime: "4 min read",
          type: "guide"
        },
        {
          title: "Performance Optimization",
          description: "Tips for improving platform performance",
          readTime: "6 min read",
          type: "guide"
        },
        {
          title: "Error Messages Explained",
          description: "Understanding and resolving error messages",
          readTime: "5 min read",
          type: "guide"
        }
      ]
    },
  ];

  const popularArticles = [
    {
      title: "How to create your first automated test",
      category: "Test Creation",
      readTime: "5 min read",
      views: "2.1k views",
      link: "#"
    },
    {
      title: "Understanding candidate assessment results",
      category: "Test Creation",
      readTime: "3 min read",
      views: "1.8k views",
      link: "#"
    },
    {
      title: "Setting up team permissions and roles",
      category: "Account Management",
      readTime: "4 min read",
      views: "1.5k views",
      link: "#"
    },
    {
      title: "API Integration Guide",
      category: "Integrations",
      readTime: "10 min read",
      views: "1.2k views",
      link: "#"
    },
    {
      title: "Data security and compliance",
      category: "Security & Privacy",
      readTime: "6 min read",
      views: "1.1k views",
      link: "#"
    },
  ];

  const faqs = [
    {
      question: "How does automated test creation work?",
      answer:
        "Our system analyzes your job description and automatically creates relevant assessments based on the required skills, experience level, and industry standards. The system uses intelligent processing to understand the key competencies needed and generates appropriate questions across technical skills, problem-solving, and behavioral assessments. You can then customize these questions, adjust difficulty levels, and add your own questions to create the perfect assessment for your needs.",
    },
    {
      question: "Can I customize the automated tests?",
      answer:
        "Absolutely! While our system creates a solid foundation, you have full control to edit questions, add your own content, adjust difficulty levels, and set custom scoring criteria. You can also save frequently used questions to your custom question bank for future use. Our platform supports multiple question types including multiple choice, coding challenges, scenario-based questions, and more.",
    },
    {
      question: "How accurate are the assessment results?",
      answer:
        "Our assessments maintain a 95% accuracy rate through continuous machine learning improvements and validation against real-world job performance data. We regularly update our algorithms based on hiring outcomes and candidate feedback to ensure the highest quality results. All assessments go through rigorous quality checks before being made available.",
    },
    {
      question: "What types of assessments can I create?",
      answer:
        "VetriaAI supports multiple assessment types including technical skills tests, cognitive assessments, personality evaluations, scenario-based challenges, coding challenges, and intelligent video interviews. You can combine different types to create comprehensive evaluation experiences that give you a complete picture of each candidate's capabilities.",
    },
    {
      question: "Is my candidate data secure?",
      answer:
        "Yes, we take data security very seriously. All data is encrypted in transit and at rest using AES-256 encryption. We follow industry best practices and comply with applicable data protection laws, including the Information Technology Act, 2000 (India). We undergo regular security audits and your candidate data is never shared with third parties without explicit consent. Our 10-layer proctoring system ensures test integrity while maintaining candidate privacy.",
    },
    {
      question: "How do I invite candidates to take assessments?",
      answer:
        "You can invite candidates through multiple methods: direct email invitations sent from the platform, shareable assessment links that can be shared via any channel, integration with your existing ATS for seamless workflow, or embedded widgets on your career page. Candidates receive clear instructions and can complete assessments on any device - desktop, tablet, or mobile.",
    },
    {
      question: "Can I integrate VetriaAI with my existing tools?",
      answer:
        "Yes, we offer integrations with popular ATS platforms, HRIS systems, and productivity tools. Our RESTful API allows for custom integrations, and we provide webhooks for real-time data synchronization. We support integrations with platforms like Workday, Greenhouse, Lever, and many others. Contact our support team for specific integration requirements or check our API documentation.",
    },
    {
      question: "What support is available if I need help?",
      answer:
        "We offer multiple support channels: 24/7 chat support via WhatsApp, email support with response within 2 hours, phone support for Enterprise customers, comprehensive documentation and guides, video tutorials, and webinar training sessions. Our customer success team is also available to help with onboarding and best practices. All support resources are available in this Help Center.",
    },
    {
      question: "How does the proctoring system work?",
      answer:
        "Our advanced 10-layer proctoring system includes camera monitoring, screen recording, tab switching detection, copy-paste prevention, fullscreen enforcement, audio monitoring, device detection, IP tracking, time tracking, and violation reporting. The system ensures test integrity while respecting candidate privacy. All proctoring data is securely stored and only accessible to authorized HR personnel.",
    },
    {
      question: "Can candidates retake assessments?",
      answer:
        "Yes, you can configure retake settings for each assessment. You can allow unlimited retakes, limit to a specific number, or disable retakes entirely. For job seekers, some assessments may have retake options after a cooldown period. All retake policies are clearly communicated to candidates before they start the assessment.",
    },
  ];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const toggleCategory = (categoryId) => {
    setOpenCategory(openCategory === categoryId ? null : categoryId);
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Modern Navigation - Simplified */}
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-0 -ml-1 group">
            <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent group-hover:from-red-800 group-hover:via-primary group-hover:to-red-800 transition-all duration-300">
              VetriaAI
            </span>
          </Link>
          <Link
            to="/login"
            className="bg-primary text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-button hover:bg-primary-hover transition-all duration-300 font-semibold text-sm sm:text-base shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            Sign In
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-blue-50 opacity-50"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(185,28,28,0.1),transparent_50%)]"></div>

        <div className="relative max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full mb-4 sm:mb-6">
            <HelpCircle className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
          </div>
          <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-gray-200 mb-4 sm:mb-6">
            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
            <span className="text-xs sm:text-sm font-semibold text-gray-700">Help Center</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-2">
            How can we <span className="text-primary">help you?</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
            Find answers to your questions, learn how to use VetriaAI
            effectively, and get the support you need to transform your hiring
            process.
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
            <div className="relative">
              <Search className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search for help articles, FAQs, or topics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 sm:py-4 text-base sm:text-lg border border-gray-300 rounded-button focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link
              to="/login"
              className="bg-primary text-white px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-primary-hover transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center justify-center shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              Sign In <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
            <button
              onClick={() => setIsModalOpen(true)}
              className="border border-gray-300 text-gray-700 px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-gray-50 transition-all duration-300 font-semibold text-base sm:text-lg"
            >
              Book Your Demo
            </button>
          </div>
        </div>
      </section>

      {/* Categories with Expandable Articles */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Documentation</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Browse by Category
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              Find the information you need organized by topic
            </p>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {categories.map((category) => {
              const Icon = category.icon;
              const isOpen = openCategory === category.id;
              return (
                <div
                  key={category.id}
                  className="bg-white rounded-card shadow-card border border-gray-100 hover:shadow-xl transition-all duration-300"
                >
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className="w-full p-6 sm:p-8 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4 sm:space-x-6">
                      <div className={`w-12 h-12 sm:w-14 sm:h-14 ${category.color} rounded-card flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                          {category.title}
                        </h3>
                        <p className="text-sm sm:text-base text-gray-600 mb-2">{category.description}</p>
                        <span className="text-xs sm:text-sm text-gray-500">
                          {category.articles} articles
                        </span>
                      </div>
                    </div>
                    {isOpen ? (
                      <ChevronDown className="w-5 h-5 text-primary flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="px-6 sm:px-8 pb-6 sm:pb-8 border-t border-gray-100">
                      <div className="pt-6 space-y-3">
                        {category.articlesList.map((article, index) => (
                          <a
                            key={index}
                            href={article.link || "#"}
                            className="block p-4 bg-gray-50 rounded-card hover:bg-gray-100 transition-colors group"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 group-hover:text-primary transition-colors">
                                    {article.title}
                                  </h4>
                                  {article.type === "documentation" && (
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                      API
                                    </span>
                                  )}
                                  {article.type === "tutorial" && (
                                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                      Tutorial
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm sm:text-base text-gray-600 mb-2">{article.description}</p>
                                <div className="flex items-center gap-4 text-xs sm:text-sm text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {article.readTime}
                                  </span>
                                  {article.type === "documentation" && (
                                    <span className="flex items-center gap-1">
                                      <Code className="w-3 h-3" />
                                      API Docs
                                    </span>
                                  )}
                                </div>
                              </div>
                              <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors ml-4 flex-shrink-0" />
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popular Articles */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 via-white to-gray-50 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Popular</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Popular Articles
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              Most viewed help articles from our community
            </p>
          </div>
          <div className="space-y-3 sm:space-y-4">
            {popularArticles.map((article, index) => (
              <a
                key={index}
                href={article.link}
                className="block bg-white p-5 sm:p-6 rounded-card shadow-card border border-gray-100 hover:shadow-xl transition-all duration-300 cursor-pointer group hover:-translate-y-1"
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                  <div className="flex-1">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                      {article.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                      <span className="px-2 py-1 bg-gray-100 rounded-button">
                        {article.category}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {article.readTime}
                      </span>
                      <span>{article.views}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">FAQs</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Frequently Asked Questions
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              Quick answers to common questions about VetriaAI
            </p>
          </div>
          <div className="space-y-3 sm:space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-card shadow-card hover:shadow-md transition-all duration-300"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full p-4 sm:p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </h3>
                  {openFAQ === index ? (
                    <ChevronDown className="w-5 h-5 text-primary flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Tickets Section */}
      {user && (
        <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full mb-4 sm:mb-6">
                <TicketIcon className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
              </div>
              <div className="inline-flex items-center justify-center px-3 sm:px-4 py-1 sm:py-1.5 bg-primary/10 rounded-full mb-3 sm:mb-4">
                <span className="text-xs sm:text-sm font-semibold text-primary">Support Tickets</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
                Get Help & Track Your Requests
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Submit a support ticket for any problem or doubt. You can later check its progress under <strong>Help / Support → Your tickets</strong>.
              </p>
            </div>

            <div className="bg-white rounded-card shadow-card border border-gray-200 p-6 sm:p-8 mb-6">
              <button
                onClick={() => setShowTicketModal(true)}
                className="w-full sm:w-auto bg-primary text-white px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-red-800 transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center justify-center shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <Plus className="w-5 h-5 mr-2" />
                Raise a new ticket
              </button>
            </div>

            {/* Your Tickets Section */}
            <div className="bg-white rounded-card shadow-card border border-gray-200 p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Your tickets</h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    View the status and progress of all your support tickets here.
                  </p>
                </div>
                <button
                  onClick={() => setShowTickets(!showTickets)}
                  className="text-primary hover:text-red-800 transition-colors font-medium"
                >
                  {showTickets ? "Hide" : "View"} Tickets
                </button>
              </div>

              {showTickets && (
                <div className="mt-6">
                  {loadingTickets ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : userTickets.length === 0 ? (
                    <div className="text-center py-12">
                      <TicketIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">No tickets yet</p>
                      <p className="text-sm text-gray-500">Click "Raise a new ticket" to get started</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {userTickets.map((ticket) => {
                        const getStatusColor = (status) => {
                          switch (status) {
                            case "open":
                              return "bg-blue-100 text-blue-800";
                            case "in_progress":
                              return "bg-yellow-100 text-yellow-800";
                            case "resolved":
                              return "bg-green-100 text-green-800";
                            case "closed":
                              return "bg-gray-100 text-gray-800";
                            default:
                              return "bg-gray-100 text-gray-800";
                          }
                        };

                        const getPriorityColor = (priority) => {
                          switch (priority) {
                            case "urgent":
                              return "text-red-600";
                            case "high":
                              return "text-orange-600";
                            case "medium":
                              return "text-blue-600";
                            case "low":
                              return "text-gray-600";
                            default:
                              return "text-gray-600";
                          }
                        };

                        return (
                          <div
                            key={ticket.id}
                            className="border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                  <h4 className="text-lg font-semibold text-gray-900">{ticket.subject}</h4>
                                  <span
                                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                      ticket.status
                                    )}`}
                                  >
                                    {ticket.status.replace("_", " ").toUpperCase()}
                                  </span>
                                  <span className={`text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                                    {ticket.priority.toUpperCase()}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{ticket.description}</p>
                                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {ticket.createdAt
                                      ? new Date(
                                        ticket.createdAt.seconds
                                          ? ticket.createdAt.seconds * 1000
                                          : ticket.createdAt
                                      ).toLocaleDateString()
                                      : "N/A"}
                                  </span>
                                  <span className="capitalize">Type: {ticket.type}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Contact Support */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white to-gray-50 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Support</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Still Need Help?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              Our support team is here to help you succeed
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            <div className="bg-white p-6 sm:p-8 rounded-card shadow-card text-center border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <MessageCircle className="w-10 h-10 sm:w-12 sm:h-12 text-primary mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">
                Live Chat
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6">
                Get instant help from our support team
              </p>
              <a
                href="https://wa.me/918019430022"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-button hover:bg-primary-hover transition-all duration-300 font-medium inline-block shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Start Chat
              </a>
              <p className="text-xs sm:text-sm text-gray-500 mt-2">Available 24/7</p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-card shadow-card text-center border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <Mail className="w-10 h-10 sm:w-12 sm:h-12 text-primary mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">
                Email Support
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6">Send us a detailed message</p>
              <a
                href="mailto:info@vetriaai.com"
                className="bg-primary text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-button hover:bg-primary-hover transition-all duration-300 font-medium inline-block shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Send Email
              </a>
              <p className="text-xs sm:text-sm text-gray-500 mt-2">
                Response within 2 hours
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-card shadow-card text-center border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <Phone className="w-10 h-10 sm:w-12 sm:h-12 text-primary mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">
                Phone Support
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6">
                Speak directly with our experts
              </p>
              <a
                href="tel:+918019430022"
                className="bg-primary text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-button hover:bg-primary-hover transition-all duration-300 font-medium inline-block shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Call Now
              </a>
              <p className="text-xs sm:text-sm text-gray-500 mt-2">
                Enterprise customers only
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-red-800 to-red-900"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>

        <div className="relative max-w-4xl mx-auto text-center text-white">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 px-2">Ready to Get Started?</h2>
          <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-95 px-4">
            Join companies using VetriaAI to transform their hiring process.
          </p>
          <Link
            to="/login"
            className="group bg-white text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-gray-50 transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            Sign In <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Raise Ticket Modal */}
      {user && (
        <RaiseTicketModal
          isOpen={showTicketModal}
          onClose={() => {
            setShowTicketModal(false);
            loadUserTickets(); // Refresh tickets after creating a new one
          }}
          userType={user.userType || "jobseeker"}
        />
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="md:col-span-1">
              <Link to="/" className="mb-4 inline-block flex items-center gap-0 -ml-1">
                <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8" />
                <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-red-400 to-primary bg-clip-text text-transparent">
                  VetriaAI
                </span>
              </Link>
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                Revolutionizing recruitment with intelligent automation.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Product</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/features" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Company</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/about" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Support</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/help-center" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Help Center
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/cancellations-refunds" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Cancellations &amp; Refunds
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/shipping" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Digital Delivery
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p className="text-sm sm:text-base">&copy; 2025 VetriaAI. All copyrights are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>

      <BookDemoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default HelpCenter;
